import update from 'immutability-helper';
import {
  BulkDisbursementData,
  BulkDisbursementListResponseData,
} from 'src/models/BulkDisburse/BulkDisbursementData';
import { ActionType, defaultAction } from 'src/redux/types';
import { getTranslation } from 'src/utilities/translator';
import { B2xTierPricing } from '../Create/types';
import { BulkDisbursementDetailTransactionData } from '../types';
import { ACTION_TYPES } from './types';

export type ColumnConfigSchema = {
  key: string;
  label: string;
};

export type ConfigSchemaData = {
  columns: Array<ColumnConfigSchema>;
  schema: string;
};

export type BulkDisbursementListState = {
  data: BulkDisbursementListResponseData;
  error: string;
  isLoading: boolean;
  totalEntry: number;
  showCustomColumnModal: boolean;
  displayedColumns: ConfigSchemaData['columns'];
  showChooseCreateModal: boolean;
};

export type BulkDisbursementDetailState = {
  data: BulkDisbursementData | null;
  error: string;
  isLoading: boolean;
  showCustomColumnModal: boolean;
  displayedColumns: ConfigSchemaData['columns'];
};

export type BulkDisbursementDetailTransactionState = {
  data: BulkDisbursementDetailTransactionData[];
  error: string;
  isLoading: boolean;
  totalEntry: number;
};

export type AdminFeePreCreationState = {
  adminFeeTier: B2xTierPricing[];
  numberOfSuccessTx: number;
};

export type BulkDisbursementState = {
  list: BulkDisbursementListState;
  detail: BulkDisbursementDetailState;
  detailTransaction: BulkDisbursementDetailTransactionState;
  adminFeePreCreation: AdminFeePreCreationState;
};

const initialState: BulkDisbursementState = {
  list: {
    data: {
      data: [],
      limit: 10,
      page: 0,
      total: 0,
    },
    displayedColumns: [
      {
        key: 'status',
        label: getTranslation('common_status'),
      },
      {
        key: 'amount',
        label: getTranslation('common_totalAmount'),
      },
      {
        key: 'campaign_id',
        label: getTranslation('bulkDisbursement_listPage_campaignID'),
      },
      {
        key: 'start',
        label: getTranslation('common_createdOn'),
      },
      {
        key: 'scheduled_time',
        label: getTranslation('common_executionTime'),
      },
      {
        key: 'name',
        label: getTranslation('disbursement_campaignName'),
      },
      {
        key: 'description',
        label: getTranslation('common_description'),
      },
      {
        key: 'total_member',
        label: getTranslation('bulkDisbursement_listPage_totalRecipients'),
      },
      {
        key: 'success',
        label: getTranslation('bulkDisbursement_listPage_numberSuccess'),
      },
      {
        key: 'failed',
        label: getTranslation('bulkDisbursement_listPage_numberFailed'),
      },
      {
        key: 'pending',
        label: getTranslation('bulkDisbursement_listPage_numberPending'),
      },
      {
        key: 'category_id',
        label: getTranslation('txCategory_title'),
      },
    ],
    showCustomColumnModal: false,
    error: '',
    isLoading: false,
    totalEntry: 0,
    showChooseCreateModal: false,
  },
  detail: {
    data: null,
    displayedColumns: [
      {
        key: 'transaction_id',
        label: getTranslation('common_transactionID'),
      },
      {
        key: 'name',
        label: getTranslation('disbursementBulk_recipient_name'),
      },
      {
        key: 'bank_receiver',
        label: getTranslation('common_bankName'),
      },
      {
        key: 'account_receiver',
        label: getTranslation('common_bankAccountNumber'),
      },
      {
        key: 'amount',
        label: getTranslation('common_amount'),
      },
      {
        key: 'admin_fee',
        label: getTranslation('common_adminFee'),
      },
      {
        key: 'ppn_tax',
        label: getTranslation('common_tax'),
      },
      {
        key: 'total_amount',
        label: getTranslation('common_totalAmount'),
      },
      {
        key: 'status',
        label: 'Status',
      },
      {
        key: 'failedReason',
        label: getTranslation('common_failedReason'),
      },
      {
        key: 'email',
        label: getTranslation('common_email'),
      },
      {
        key: 'phone_number',
        label: getTranslation('common_phoneNumber'),
      },
      {
        key: 'tx_note',
        label: getTranslation('common_transactionNote'),
      },
      {
        key: 'client_internal_note',
        label: getTranslation('disbursementBulk_internal_notes'),
      },
    ],
    showCustomColumnModal: false,
    isLoading: false,
    error: '',
  },
  detailTransaction: {
    data: [],
    isLoading: false,
    error: '',
    totalEntry: 0,
  },
  adminFeePreCreation: {
    adminFeeTier: [],
    numberOfSuccessTx: 0,
  },
};

export default (state = initialState, action: ActionType = defaultAction) => {
  const { payload, type } = action;

  switch (type) {
    case ACTION_TYPES.BULK_DISBURSEMENT_LIST:
      return update(state, {
        list: {
          isLoading: {
            $set: true,
          },
        },
      });
    case ACTION_TYPES.BULK_DISBURSEMENT_LIST_CREATE:
      return update(state, {
        list: {
          showChooseCreateModal: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.BULK_DISBURSEMENT_LIST_SUCCESS:
      return update(state, {
        list: {
          data: {
            $set: payload.data,
          },
          error: {
            $set: '',
          },
          isLoading: {
            $set: false,
          },
          totalEntry: {
            $set: payload.data.total,
          },
        },
      });
    case ACTION_TYPES.BULK_DISBURSEMENT_LIST_FAILED:
      return update(state, {
        list: {
          error: {
            $set: payload.message,
          },
          isLoading: {
            $set: false,
          },
        },
      });
    case ACTION_TYPES.BULK_DISBURSEMENT_LIST_COLUMN:
      return update(state, {
        list: {
          displayedColumns: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.BULK_DISBURSEMENT_LIST_SHOW_MODAL:
      return update(state, {
        list: {
          showCustomColumnModal: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.BULK_DISBURSEMENT_DETAIL:
      return update(state, {
        detail: {
          isLoading: {
            $set: true,
          },
        },
      });
    case ACTION_TYPES.BULK_DISBURSEMENT_DETAIL_SUCCESS:
      return update(state, {
        detail: {
          data: {
            $set: payload.data,
          },
          error: {
            $set: '',
          },
          isLoading: {
            $set: false,
          },
        },
      });
    case ACTION_TYPES.BULK_DISBURSEMENT_DETAIL_FAILED:
      return update(state, {
        detail: {
          error: {
            $set: payload.message,
          },
          isLoading: {
            $set: false,
          },
        },
      });
    case ACTION_TYPES.BULK_DISBURSEMENT_DETAIL_COLUMN:
      return update(state, {
        detail: {
          displayedColumns: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.BULK_DISBURSEMENT_DETAIL_SHOW_MODAL:
      return update(state, {
        detail: {
          showCustomColumnModal: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.BULK_DISBURSEMENT_DETAIL_TRANSACTION:
      return update(state, {
        detailTransaction: {
          isLoading: {
            $set: true,
          },
        },
      });
    case ACTION_TYPES.BULK_DISBURSEMENT_DETAIL_TRANSACTION_SUCCESS: {
      const detailTransactionResult = payload.data.map((data: any) => ({
        phone_number: data.phone,
        bank_receiver: data.account_bank,
        name: data.name,
        account_receiver: data.account_number,
        account_name: data.account_name,
        last_updated: data.lastUpdated,
        amount: data.amount,
        admin_fee: data.adminFee,
        ppn_tax: data.ppn_tax,
        status: data.status,
        email: data.email,
        error_mapping: data.error_mapping,
        tx_note: data.tx_note,
        transaction_id: data.transaction_id,
        client_internal_note: data.client_internal_note,
        id: data.id,
        campaign_id: data.campaign_id,
      }));
      return update(state, {
        detailTransaction: {
          $set: {
            data: detailTransactionResult,
            error: '',
            isLoading: false,
            totalEntry: detailTransactionResult.length,
          },
        },
      });
    }
    case ACTION_TYPES.BULK_DISBURSEMENT_DETAIL_TRANSACTION_FAILED:
      return update(state, {
        detailTransaction: {
          error: {
            $set: payload.message,
          },
          isLoading: {
            $set: false,
          },
        },
      });
    case ACTION_TYPES.ADMIN_FEE_PRECREATION_SUCCESS:
      return update(state, {
        adminFeePreCreation: {
          $set: {
            adminFeeTier: payload.data.tierPricingList.reverse(),
            numberOfSuccessTx: payload.data.numberOfSuccessTx,
          },
        },
      });
    default:
      return state;
  }
};
